import React, { useEffect, useMemo, useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LocalizationProvider, StaticDatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Button } from 'components/ui';
import { textSizeByBreakpoint } from 'utils/helper';

type Props = {
  open: boolean;
  startDate?: Date;
  endDate?: Date;
  onClose: () => void;
  onApply: (startDate: Date, endDate: Date) => void;
};

const DEFAULT_RANGE_DAYS = 7;

const DateRangePickerDialog = ({ open, startDate, endDate, onClose, onApply }: Props) => {
  const theme = useTheme();
  const [draftStart, setDraftStart] = useState<Date | null>(null);
  const [draftEnd, setDraftEnd] = useState<Date | null>(null);

  useEffect(() => {
    if (!open) return;

    if (startDate && endDate) {
      setDraftStart(startDate);
      setDraftEnd(endDate);
      return;
    }

    const now = new Date();
    setDraftEnd(now);
    setDraftStart(new Date(now.getTime() - DEFAULT_RANGE_DAYS * 24 * 60 * 60 * 1000));
  }, [open, startDate, endDate]);

  const errorText = useMemo(() => {
    if (!draftStart || !draftEnd) return '';
    if (draftEnd.getTime() < draftStart.getTime()) return 'End date must be after start date';
    return '';
  }, [draftStart, draftEnd]);

  const canApply = Boolean(draftStart && draftEnd && !errorText);

  const handleApply = () => {
    if (!draftStart || !draftEnd) return;
    onApply(draftStart, draftEnd);
  };

  const calendarSx = {
    '& .MuiPickersDay-root.Mui-selected': {
      backgroundColor: theme.palette.primary.main
    },
    '& .MuiPickersDay-root.Mui-selected:hover': {
      backgroundColor: theme.palette.primary.main,
      opacity: 0.9
    },
    '& .MuiPickersDay-root.Mui-selected:focus': {
      backgroundColor: theme.palette.primary.main
    },
    '& .MuiPickersDay-today:not(.Mui-selected)': {
      borderColor: theme.palette.primary.main
    }
  } as const;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: { xs: '100%', sm: 860 },
          borderRadius: '14px',
          border: '1px solid',
          borderColor: theme.palette.border.main,
          backgroundColor: theme.palette.background.default
        }
      }}
    >
      <DialogTitle sx={{ pb: 1.5 }}>
        <Typography fontSize={textSizeByBreakpoint(18)} fontWeight={700}>
          Custom Range
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              divider={<Divider flexItem orientation="vertical" sx={{ borderColor: theme.palette.border.main, opacity: 1 }} />}
              sx={{
                '& .MuiPickersToolbar-root': { display: 'none' },
                '& .MuiPickersStaticWrapper-root': {
                  backgroundColor: theme.palette.background.default
                }
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography fontSize={textSizeByBreakpoint(14)} fontWeight={600} sx={{ mb: 1, color: theme.palette.text.secondary }}>
                  Start
                </Typography>
                <Box sx={calendarSx}>
                  <StaticDatePicker
                    displayStaticWrapperAs="desktop"
                    value={draftStart}
                    onChange={(newValue) => {
                      setDraftStart(newValue);
                      if (newValue && draftEnd && draftEnd.getTime() < newValue.getTime()) {
                        setDraftEnd(newValue);
                      }
                    }}
                    renderInput={() => null as any}
                    componentsProps={{
                      actionBar: { actions: [] }
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography fontSize={textSizeByBreakpoint(14)} fontWeight={600} sx={{ mb: 1, color: theme.palette.text.secondary }}>
                  End
                </Typography>
                <Box sx={calendarSx}>
                  <StaticDatePicker
                    displayStaticWrapperAs="desktop"
                    value={draftEnd}
                    onChange={(newValue) => setDraftEnd(newValue)}
                    renderInput={() => null as any}
                    componentsProps={{
                      actionBar: { actions: [] }
                    }}
                  />
                </Box>
              </Box>
            </Stack>

            {errorText ? (
              <Typography fontSize={12} color={theme.palette.error.main}>
                {errorText}
              </Typography>
            ) : (
              <Box />
            )}

            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
              <Button
                variant="outlined"
                color="primary"
                onClick={onClose}
                sx={{ borderRadius: '10px', borderColor: theme.palette.border.main, color: theme.palette.text.primary }}
              >
                Cancel
              </Button>
              <Button variant="contained" color="primary" onClick={handleApply} disabled={!canApply} sx={{ borderRadius: '10px' }}>
                Apply
              </Button>
            </Stack>
          </Stack>
        </LocalizationProvider>
      </DialogContent>
    </Dialog>
  );
};

export default DateRangePickerDialog;
